<template>
  <b-card
    title="LMS Configurations"
    tag="article"
    class="mb-2 content-wrapper"
  >
    <!-- View Mode -->
    <transition
      name="fade-slide"
      mode="out-in"
    >
      <div
        v-if="isLoading || isUpdatingLmsEnabled"
        key="loader"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          label="Loading..."
        />
      </div>
      <div
        v-else
        key="content"
      >
        <b-form-group>
          <div class="d-flex align-items-center">
            <b-form-radio-group
              v-model="lmsEnabled"
              name="lms-config-radios"
              :disabled="isUpdatingLmsEnabled"
            >
              <b-form-radio :value="0">
                Disabled
              </b-form-radio>
              <b-form-radio :value="1">
                Enabled
              </b-form-radio>
            </b-form-radio-group>
          </div>
        </b-form-group>

        <div v-if="lmsEnabled == 1">
          <div
            v-if="lmsConfigMode === 'view' && lmsConfigurations"
            key="view"
          >
            <div class="d-flex justify-content-end mb-3">
              <b-button
                variant="outline-primary"
                size="sm"
                class="btn btn-primary rounded d-flex align-items-center gap-2 py-75 px-1 rounded-pill"
                @click="lmsConfigMode = 'edit'"
              >
                Edit Configurations
              </b-button>
            </div>

            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">LMS Provider</span>
                <span class="info-value">
                  {{
                    lmsConfigurationsTypesLookups.find(
                      (t) => t.id === lmsConfigurations.type,
                    )
                      ? lmsConfigurationsTypesLookups.find(
                        (t) => t.id === lmsConfigurations.type,
                      ).label
                      : lmsConfigurations.type
                  }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">LMS URL</span>
                <span class="info-value">{{ lmsConfigurations.url }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Token</span>
                <!-- Masking Token for security visual -->
                <span class="info-value masked">
                  {{ lmsConfigurations.token }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">Secret Key</span>
                <!-- Masking Secret Key for security visual -->
                <span class="info-value masked">
                  {{ lmsConfigurations.secret_key }}
                </span>
              </div>
            </div>
          </div>

          <!-- Edit Mode -->
          <div
            v-else
            key="edit"
          >
            <validation-observer ref="lmsConfigObserver">
              <b-form
                class="ai-config-form"
                autocomplete="off"
                @submit.prevent="submitLMSForm"
              >
                <b-row>
                  <b-col md="6">
                    <b-form-group
                      label="LMS Provider"
                      label-for="lmsType"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Type"
                        rules="required"
                      >
                        <v-select
                          id="lmsType"
                          v-model="LmsForm.type"
                          name="lmsType"
                          :options="lmsConfigurationsTypesLookups"
                          :disabled="lmsConfigurationsTypesLookups.length === 0"
                          autocomplete="off"
                          :reduce="(type) => type"
                          label="label"
                        />
                        <small
                          class="form-hint"
                        >Select LMS configuration type</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="LMS URL"
                      label-for="moodleUrl"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Moodle URL"
                        rules="required"
                      >
                        <b-form-input
                          id="moodleUrl"
                          v-model="LmsForm.url"
                          name="moodleUrl"
                          placeholder="https://moodle/canvas.example.com"
                        />
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Token"
                      label-for="lmsToken"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Token"
                        rules="required"
                      >
                        <b-form-input
                          id="lmsToken"
                          v-model="LmsForm.token"
                          name="lmsToken"
                          placeholder="Enter your Token"
                          type="text"
                        />
                        <small
                          class="form-hint"
                        >Your secure Token for LMS services</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Secret Key"
                      label-for="secretKey"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Secret Key"
                        rules="required"
                      >
                        <b-form-input
                          id="secretKey"
                          v-model="LmsForm.secret_key"
                          name="secretKey"
                          placeholder="Enter Secret Key"
                          type="text"
                        />
                        <small class="form-hint">Your secure Secret Key</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="form-actions d-flex justify-content-end gap-2">
                  <b-button
                    v-if="lmsConfigurations && lmsConfigurations.id"
                    variant="secondary"
                    class="mr-1 rounded-pill"
                    :disabled="isSubmittingLms"
                    @click="cancelEditLMS"
                  >
                    Cancel
                  </b-button>
                  <b-button
                    type="submit"
                    variant="primary"
                    class="submit-btn rounded-pill"
                    :disabled="isSubmittingLms"
                  >
                    <span v-if="!isSubmittingLms">Save LMS Configurations</span>
                    <b-spinner
                      v-else
                      small
                    />
                  </b-button>
                </div>
              </b-form>
            </validation-observer>
          </div>
        </div>
      </div>
    </transition>
  </b-card>
</template>

<script>
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
  BForm,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormRadioGroup,
} from 'bootstrap-vue'

export default {
  name: 'InstitutionLmsConfiguration',
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    vSelect,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    institutionId: {
      required: true,
    },
    institution: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      lmsConfigurations: 'institutions/lmsConfigurations',
      lmsConfigurationsTypesLookups:
        'institutions/lmsConfigurationsTypesLookups',
    }),
    lmsEnabled: {
      get() {
        return this.institution ? Number(this.institution.lms_enabled) : 0
      },
      set(value) {
        this.updateLmsEnabled(value)
      },
    },
  },

  data() {
    return {
      lmsFormMode: 'add',
      isSubmittingLms: false,
      LmsForm: {
        url: '',
        token: '',
        secret_key: '',
        type: null,
      },
      lmsConfigMode: 'view',
      isLoading: false,
      isUpdatingLmsEnabled: false,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.isLoading = true
      this.$store.dispatch('institutions/lmsConfigurationsTypesLookups')
      this.$store
        .dispatch('institutions/lmsConfigurations', this.institutionId)
        .then(() => {
          if (this.lmsConfigurations && this.lmsConfigurations.id) {
            this.lmsFormMode = 'edit'
            this.lmsConfigMode = 'view'
            this.populateLMSForm()
          } else {
            this.lmsFormMode = 'add'
            this.lmsConfigMode = 'edit'
            this.LmsForm = {
              url: '',
              token: '',
              secret_key: '',
              type: null,
            }
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateLmsEnabled(value) {
      this.isUpdatingLmsEnabled = true
      this.$store
        .dispatch('institutions/updateLmsEnabled', {
          id: this.institutionId,
          query: { lms_enabled: value },
        })
        .then(() => {
          this.$store
            .dispatch('institutions/get', this.institutionId)
            .then(() => {
              this.isUpdatingLmsEnabled = false
            })
        })
        .catch(error => {
          this.isUpdatingLmsEnabled = false
          let errorMsg = 'An error occurred.'
          if (error?.response?.data?.errors) {
            errorMsg = Object.entries(error.response.data.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('\n')
          } else if (error?.response?.data?.message) {
            errorMsg = error.response.data.message
          }
          this.$swal({
            title: 'Error!',
            text: errorMsg,
            icon: 'error',
            confirmButtonText: 'OK',
          })
        })
    },
    populateLMSForm() {
      this.LmsForm = {
        url: this.lmsConfigurations.url || '',
        token: this.lmsConfigurations.token || '',
        secret_key: this.lmsConfigurations.secret_key || '',
        institution_id: this.institutionId,
        id: this.lmsConfigurations.id,
        type: this.lmsConfigurations.type,
      }
    },
    cancelEditLMS() {
      this.lmsConfigMode = 'view'
      this.populateLMSForm()
    },
    submitLMSForm() {
      this.$refs.lmsConfigObserver.validate().then(success => {
        if (success) {
          this.isSubmittingLms = true
          if (this.lmsFormMode === 'edit') {
            this.$store
              .dispatch('institutions/updateLmsConfigurations', this.LmsForm)
              .then(() => {
                this.isSubmittingLms = false
                this.$swal({
                  title: 'Success!',
                  text: 'LMS configurations updated successfully.',
                  icon: 'success',
                  confirmButtonText: 'OK',
                })
                this.init()
              })
              .catch(error => {
                let errorMsg = 'An error occurred.'
                if (error?.response?.data?.errors) {
                  errorMsg = Object.entries(error.response.data.errors)
                    .map(
                      ([field, messages]) => `${field}: ${messages.join(', ')}`,
                    )
                    .join('\n')
                } else if (error?.response?.data?.message) {
                  errorMsg = error.response.data.message
                }
                this.$swal({
                  title: 'Error!',
                  text: errorMsg,
                  icon: 'error',
                  confirmButtonText: 'OK',
                })
                this.isSubmittingLms = false
              })
          } else {
            this.LmsForm.institution_id = this.institutionId
            this.$store
              .dispatch('institutions/storeLmsConfigurations', this.LmsForm)
              .then(() => {
                this.isSubmittingLms = false
                this.$swal({
                  title: 'Success!',
                  text: 'LMS configurations saved successfully.',
                  icon: 'success',
                  confirmButtonText: 'OK',
                })
                this.init()
              })
              .catch(error => {
                let errorMsg = 'An error occurred.'
                if (error?.response?.data?.errors) {
                  errorMsg = Object.entries(error.response.data.errors)
                    .map(
                      ([field, messages]) => `${field}: ${messages.join(', ')}`,
                    )
                    .join('\n')
                } else if (error?.response?.data?.message) {
                  errorMsg = error.response.data.message
                }
                this.$swal({
                  title: 'Error!',
                  text: errorMsg,
                  icon: 'error',
                  confirmButtonText: 'OK',
                })
                this.isSubmittingLms = false
              })
          }
        } else {
          this.$swal({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please fill in all required fields.',
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.content-wrapper {
  background: #000a1e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  color: #e9ecef;
  animation: slideInLeft 0.5s ease forwards;

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.info-item {
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 10px;
  border-left: 4px solid rgba(15, 217, 222, 0.5);
  transition: transform 0.3s ease, border-color 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-left-color: rgba(133, 13, 204, 1);
    background: rgba(255, 255, 255, 0.08);
  }
}

.info-label {
  display: block;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  display: block;
  font-size: 1.1rem;
  color: #fff;
  font-weight: 500;
  word-break: break-word;
}

.masked {
  font-family: "Courier New", monospace;
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.ai-config-form {
  .modern-input {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #fff !important;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;

    &:focus {
      background: rgba(255, 255, 255, 0.08) !important;
      border-color: rgba(15, 217, 222, 0.5) !important;
      box-shadow: 0 0 0 3px rgba(15, 217, 222, 0.1) !important;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4) !important;
    }
  }

  .form-hint {
    display: block;
    margin-top: 0.5rem;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
  }
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.submit-btn {
  background: linear-gradient(
    90deg,
    rgba(133, 13, 204, 1) 0%,
    rgba(15, 217, 222, 1) 100%
  ) !important;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(133, 13, 204, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
